/**
 * lex.ivoliq Login/Registrierung (Juni 2026) — Split-Screen.
 * Links: Architektur-Bild mit Navy-Overlay + Wortmarke.
 * Rechts: Anmelden / Registrieren (AT-Rechtsanwälte).
 * (Ehemals LexLanding — die Landing ist jetzt eine eigene Conversion-Seite.)
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import "@/styles/lex.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://images.unsplash.com/photo-1479293581560-aee98bb24f7f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBnbGFzc3xlbnwwfHx8fDE3ODMzMjI4NDh8MA&ixlib=rb-4.1.0&q=85";

export default function LexLogin() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const { login, register, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLoggedOut, setShowLoggedOut] = useState(Boolean(location.state?.loggedOut));
  // Invite-Only-Pilot (Aug 2026): persönlicher Einladungslink ?invite=CODE
  const [inviteCode] = useState(() => new URLSearchParams(window.location.search).get("invite") || "");
  const [inviteInfo, setInviteInfo] = useState(null);

  useEffect(() => {
    if (!inviteCode) return;
    axios.get(`${API}/lex/invites/${inviteCode}`)
      .then((r) => {
        setInviteInfo(r.data);
        setMode("register");
        if (r.data?.state === "ready") {
          setEmail(r.data.email || "");
          setName(r.data.name || "");
        }
      })
      .catch(() => { setInviteInfo({ state: "invalid" }); setMode("register"); });
  }, [inviteCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const userData = await login(email, password, true, "lex");
        // Aug 2026 — Produkt-Trennung: ivoliq-Konten dürfen sich NICHT über
        // die lex-Maske anmelden (Gegenstück zu Login.js).
        if (userData?.account_variant !== "lex") {
          await logout();
          toast.error("Dieses Konto gehört zu ivoliq. Bitte melde dich unter ivoliq.io an.", { duration: 8000 });
          setLoading(false);
          return;
        }
        navigate("/lex");
      } else {
        if (!consent) {
          toast.error("Bitte AGB, Datenschutzerklärung und AVV akzeptieren.");
          setLoading(false);
          return;
        }
        await register(email, password, name, "lex_landing", {
          accepted_terms: true,
          accepted_privacy: true,
          accepted_avv: true,
          variant: "lex",
          invite_code: inviteCode || null,
        });
        setRegistered(true);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Anmeldung fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lex-theme min-h-screen flex" data-testid="lex-login-page">
      {/* Linke Seite — Bild + Botschaft */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "rgba(10,17,40,0.85)" }} />
        <div className="relative z-10 flex flex-col justify-between p-14 w-full">
          <div className="lex-fade-up">
            <span className="lex-serif text-3xl font-semibold text-white">lex<span style={{ color: "#C5A059" }}>.</span>ivoliq</span>
          </div>
          <div>
            <div className="lex-gold-line mb-6" />
            <h1 className="lex-serif text-4xl sm:text-5xl font-semibold text-white leading-tight lex-fade-up-1">
              Mühelose Steuerverwaltung<br />für Rechtsanwälte.
            </h1>
            <p className="text-slate-300 mt-6 max-w-md text-base lex-fade-up-2">
              Ihre Steuerberatung übernimmt alles — UVA, Pauschalierung, Honorarnoten.
              Sie haben null Aufwand.
            </p>
            <div className="mt-10 space-y-3 lex-fade-up-3">
              {["Vorsteuerpauschale 1,8 % (§ 14 UStG)", "Automatische monatliche Honorarnote", "UVA & Fristen — vollständig betreut"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-sm text-slate-200">
                  <span className="lex-mono" style={{ color: "#C5A059" }}>—</span>
                  {t}
                </div>
              ))}
            </div>
          </div>
          <p className="lex-mono text-xs text-slate-500">
            ivoliq GmbH · ATU83347308 · <Link to="/kanzlei" className="underline hover:text-slate-300" data-testid="lex-kanzlei-login-link">Kanzlei-Login</Link>
          </p>
        </div>
      </div>

      {/* Rechte Seite — Formular */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12" style={{ background: "#FDFCFB" }}>
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10">
            <span className="lex-serif text-3xl font-semibold" style={{ color: "#0A1128" }}>lex<span style={{ color: "#C5A059" }}>.</span>ivoliq</span>
          </div>

          {registered ? (
            <div className="lex-card p-8 lex-fade-up" data-testid="lex-register-success">
              <div className="lex-gold-line mb-4" />
              <h2 className="lex-serif text-2xl font-semibold" style={{ color: "#0A1128" }}>Fast geschafft.</h2>
              <p className="text-sm text-slate-600 mt-3">
                Wir haben Ihnen eine E-Mail an <span className="lex-mono">{email}</span> gesendet.
                Bitte bestätigen Sie Ihre Adresse, um fortzufahren.
              </p>
              <button className="lex-btn-outline mt-6 w-full" onClick={() => { setRegistered(false); setMode("login"); }} data-testid="lex-back-to-login-btn">
                Zur Anmeldung
              </button>
            </div>
          ) : (
            <>
              {showLoggedOut && (
                <div className="mb-6 flex items-start justify-between gap-3 border border-slate-200 bg-white px-4 py-3 lex-fade-up"
                  style={{ borderLeft: "3px solid #C5A059" }} data-testid="lex-logged-out-banner">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: "#0A1128" }}>Sie wurden abgemeldet.</p>
                    <p className="text-xs text-slate-500 mt-0.5">Zu Ihrer Sicherheit wurde die Sitzung beendet. Melden Sie sich erneut an.</p>
                  </div>
                  <button onClick={() => setShowLoggedOut(false)} className="text-slate-400 hover:text-slate-600 text-lg leading-none"
                    aria-label="Hinweis schließen" data-testid="lex-logged-out-banner-close">×</button>
                </div>
              )}
              <div className="flex border-b border-slate-200 mb-8">
                {[["login", "Anmelden"], ["register", "Persönlichen Zugang anfordern"]].map(([m, label]) => (
                  <button
                    key={m}
                    onClick={() => setMode(m)}
                    data-testid={`lex-tab-${m}`}
                    className={`px-4 py-3 text-xs uppercase tracking-widest font-semibold transition-colors border-b-2 -mb-px ${
                      mode === m ? "border-[#C5A059] text-[#0A1128]" : "border-transparent text-slate-400 hover:text-slate-600"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <h2 className="lex-serif text-3xl font-semibold mb-2" style={{ color: "#0A1128" }}>
                {mode === "login" ? "Willkommen zurück." : "Ihr Zugang als Rechtsanwalt."}
              </h2>
              <p className="text-sm text-slate-500 mb-8">
                {mode === "login"
                  ? "Melden Sie sich mit Ihren Zugangsdaten an."
                  : "In 2 Minuten eingerichtet — danach übernimmt Ihre Steuerberatung."}
              </p>

              {mode === "register" && inviteInfo?.state === "ready" && (
                <div className="mb-6 border border-slate-200 bg-white px-4 py-3 lex-fade-up" style={{ borderLeft: "3px solid #C5A059" }} data-testid="lex-invite-badge">
                  <p className="text-sm font-semibold" style={{ color: "#0A1128" }}>Persönliche Einladung</p>
                  <p className="text-xs text-slate-500 mt-0.5">Dieser Zugang ist für <span className="lex-mono">{inviteInfo.email}</span> reserviert.</p>
                </div>
              )}
              {mode === "register" && inviteCode && inviteInfo && inviteInfo.state !== "ready" && (
                <div className="mb-6 border border-slate-200 bg-white px-4 py-3" style={{ borderLeft: "3px solid #B91C1C" }} data-testid="lex-invite-invalid">
                  <p className="text-sm font-semibold" style={{ color: "#0A1128" }}>
                    {inviteInfo.state === "used" ? "Diese Einladung wurde bereits verwendet." : "Diese Einladung ist abgelaufen oder ungültig."}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">Bitte wenden Sie sich an uns — wir senden Ihnen gerne eine neue Einladung.</p>
                </div>
              )}
              {mode === "register" && !inviteCode && (
                <div className="mb-6 border border-slate-200 bg-white px-4 py-3" style={{ borderLeft: "3px solid #C5A059" }} data-testid="lex-invite-hint">
                  <p className="text-sm font-semibold" style={{ color: "#0A1128" }}>Zugang derzeit nur mit persönlicher Einladung.</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Wir starten mit einer kleinen Pilot-Gruppe. <Link to="/" className="underline">Tragen Sie sich auf die Warteliste ein</Link> — wir melden uns persönlich.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === "register" && (
                  <div>
                    <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">Name</label>
                    <input className="lex-input" value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="Dr. Max Mustermann" required data-testid="lex-name-input" />
                  </div>
                )}
                <div>
                  <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">E-Mail</label>
                  <input type="email" className="lex-input" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="kanzlei@beispiel.at" required data-testid="lex-email-input"
                    readOnly={mode === "register" && inviteInfo?.state === "ready"} />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">Passwort</label>
                  <input type="password" className="lex-input" value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••" minLength={8} required data-testid="lex-password-input" />
                </div>

                {mode === "register" && (
                  <label className="flex items-start gap-3 text-xs text-slate-600 cursor-pointer pt-1">
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5" data-testid="lex-consent-checkbox" />
                    <span>
                      Ich akzeptiere die <Link to="/terms" className="underline" target="_blank">AGB</Link>,{" "}
                      die <Link to="/privacy" className="underline" target="_blank">Datenschutzerklärung</Link>{" "}
                      und den <Link to="/avv" className="underline" target="_blank">AVV</Link>.
                    </span>
                  </label>
                )}

                <button type="submit" className="lex-btn w-full mt-2" disabled={loading} data-testid="lex-submit-btn">
                  {loading ? "Bitte warten …" : mode === "login" ? "Anmelden" : "Zugang anfordern"}
                </button>
              </form>

              {mode === "login" && (
                <p className="text-xs text-slate-400 mt-6 text-center">
                  <Link to="/forgot-password" className="underline hover:text-slate-600" data-testid="lex-forgot-password-link">Passwort vergessen?</Link>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
